import clsx from "clsx";
import { FC } from "react";
import { Decimal, useTranslation } from "rujira.ui";
import { LimitOrdersFragment$data } from "../__generated__/LimitOrdersFragment.graphql";
import { RecurringOrdersFragment$data } from "../__generated__/RecurringOrdersFragment.graphql";

type LimitOrder = LimitOrdersFragment$data[number];
type RecurringOrder = RecurringOrdersFragment$data[number];

export const LabelSide: FC<LimitOrder> = ({ side }) => {
  const { t } = useTranslation();
  return (
    <div
      className={clsx({
        "tag tag--borderless": true,
        "tag--red": side === "base",
        "tag--teal": side === "quote",
      })}>
      {side === "base" ? t("sell") : t("buy")}
    </div>
  );
};

export const LabelType: FC<LimitOrder> = ({ type }) => {
  const { t } = useTranslation();
  return (
    <div
      className={clsx({
        "tag tag--borderless": true,
        "tag--grey": true,
      })}>
      {type === "fixed" ? t("limit") : t("tracking")}
    </div>
  );
};

export const LabelStatus: FC<
  RecurringOrder & { completed?: boolean; plain?: boolean }
> = ({ status, completed, plain }) => {
  const { t } = useTranslation();
  const tone = completed ? "grey" : status === "ACTIVE" ? "teal" : "red";
  const label = completed
    ? t("completed")
    : status === "ACTIVE"
      ? t("active")
      : t("paused");

  if (plain) {
    return <span className={`mono color-${tone}`}>{label}</span>;
  }

  return <div className={`tag tag--borderless tag--${tone}`}>{label}</div>;
};

export const StatusFilled: FC<
  LimitOrder & { plain?: boolean }
> = ({ offer, remaining, plain }) => {
  const filled = Number(offer)
    ? (Number(offer) - Number(remaining)) / Number(offer)
    : 0;
  const tone = filled < 0.25 ? "red" : filled < 0.75 ? "orange" : "teal";

  if (plain) {
    return (
      <span className={`mono color-${tone}`}>
        {(filled * 100).toLocaleDecimal(2)}%
      </span>
    );
  }

  return (
    <div className={`tag tag--borderless tag--${tone}`}>
      {(filled * 100).toLocaleDecimal(2)}%
    </div>
  );
};

export const Price: FC<LimitOrder> = ({
  rate,
  deviation,
  pair,
}) => {
  switch (deviation) {
    case null:
    case undefined:
      return (
        <>
          <Decimal
            subscript
            decimals={12}
            round={4}
            amount={BigInt(rate)}
            symbol={pair.assetQuote.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
            clip
          />
        </>
      );

    default:
      return (
        <div>
          <Decimal
            subscript
            decimals={12}
            round={4}
            amount={BigInt(rate)}
            symbol={pair.assetQuote.metadata.symbol}
            symbolClassName="decimal__symbol--small color-grey condensed fw-500"
            clip
          />{" "}
          <p className="inline-block color-grey condensed fs-12 fw-500">
            ({Number(deviation) > 0 ? "+" : ""}
            {(Number(deviation) / 100).toLocaleDecimal(4)}%)
          </p>
        </div>
      );
  }
};
